import { useState, useCallback, useRef, useEffect } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import matrixService from '../services/matrixService';
import logger from '../utils/logger';

/**
 * Hook for adding a user by ID to the global contacts.
 * This creates or retrieves a direct message (DM) room with the specified user,
 * effectively adding them to the contacts list.
 * 
 * Can optionally verify user without creating a room (for contact validation).
 *
 * @returns {{
 *   addContact: (userId: string, createRoom?: boolean) => Promise<{success: boolean, roomId?: string, error?: string}>,
 *   isAdding: boolean,
 *   error: string | null
 * }}
 */
export const useAddContact = () => {
  const client = useMatrixClient();
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState(null);
  const isUnmountedRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  /**
   * Add a contact by creating or finding an existing DM room with the user.
   * 
   * @param {string} userId - The Matrix user ID to add as a contact (e.g., "@user:example.com")
   * @param {boolean} createRoom - Whether to create a room if one doesn't exist (default: true)
   * @returns {Promise<{success: boolean, roomId?: string, error?: string, isExisting?: boolean}>}
   */
  const addContact = useCallback(async (userId, createRoom = true) => {
    if (!client) {
      const errorMsg = 'Matrix client not initialized';
      logger.error('[useAddContact] Error:', errorMsg);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }

    // Validate userId format
    if (!userId || typeof userId !== 'string') {
      const errorMsg = 'Invalid user ID provided';
      logger.error('[useAddContact] Error:', errorMsg);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }

    // Ensure userId starts with @ and contains :
    if (!userId.startsWith('@') || !userId.includes(':')) {
      const errorMsg = 'Matrix ID must be in format @user:homeserver.com';
      logger.error('[useAddContact] Error:', errorMsg);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }

    // Prevent adding self as contact
    const myUserId = client.getUserId();
    if (userId === myUserId) {
      const errorMsg = 'Cannot add yourself as a contact';
      logger.warn('[useAddContact] Error:', errorMsg);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }

    if (!isUnmountedRef.current) {
      setIsAdding(true);
      setError(null);
    }

    try {
      logger.debug('[useAddContact] Adding contact:', userId);

      // Step 1: Check if a DM room already exists with this user
      const existingRoomId = await findExistingDMRoom(client, userId);

      if (existingRoomId) {
        logger.info('[useAddContact] Found existing DM room:', existingRoomId);
        if (!isUnmountedRef.current) {
          setIsAdding(false);
        }
        return {
          success: true,
          roomId: existingRoomId,
          isExisting: true,
        };
      }

      // Step 2: Verify the user exists by fetching their profile
      try {
        logger.debug('[useAddContact] Fetching profile for user:', userId);
        await client.getProfileInfo(userId);
        logger.debug('[useAddContact] User profile exists');
      } catch (profileError) {
        logger.error('[useAddContact] User not found or profile fetch failed:', profileError);
        const errorMsg = 'User not found. Please check the Matrix ID and try again.';
        if (!isUnmountedRef.current) {
          setError(errorMsg);
          setIsAdding(false);
        }
        return { success: false, error: errorMsg };
      }

      // If createRoom is false, just verify the user exists and return
      if (!createRoom) {
        logger.info('[useAddContact] User verified, skipping room creation:', userId);
        if (!isUnmountedRef.current) {
          setIsAdding(false);
        }
        return {
          success: true,
          userVerified: true,
          skipRoomCreation: true,
        };
      }

      // Step 3: Create a new DM room
      logger.debug('[useAddContact] Creating new DM room with user:', userId);
      
      const roomOptions = {
        invite: [userId],
        is_direct: true,
        preset: 'trusted_private_chat',
        visibility: 'private',
        creation_content: {
          type: 'de.gematik.tim.room',
        },
        initial_state: [
          {
            type: 'm.room.encryption',
            state_key: '',
            content: { algorithm: 'm.megolm.v1.aes-sha2' },
          },
        ],
      };

      const { room_id: roomId } = await client.createRoom(roomOptions);
      logger.debug('[useAddContact] Room created successfully:', roomId);

      // Step 4: Update m.direct account data to mark this as a DM
      logger.debug('[useAddContact] Updating m.direct account data');
      await matrixService.setDMRoom(roomId, userId);
      logger.debug('[useAddContact] m.direct updated successfully');

      // Step 5: Wait for the room to sync
      await waitForRoomSync(client, roomId);

      logger.info('[useAddContact] Contact added successfully:', userId, 'roomId:', roomId);
      if (!isUnmountedRef.current) {
        setIsAdding(false);
      }
      return {
        success: true,
        roomId,
        isExisting: false,
      };
    } catch (err) {
      const errorMsg = err.message || 'Failed to add contact';
      logger.error('[useAddContact] Error adding contact:', err);
      if (!isUnmountedRef.current) {
        setError(errorMsg);
        setIsAdding(false);
      }
      return { success: false, error: errorMsg };
    }
  }, [client]);

  return {
    addContact,
    isAdding,
    error,
  };
};

/**
 * Find an existing DM room with a specific user.
 * Checks m.direct account data for rooms with this user.
 * 
 * @param {MatrixClient} client - The Matrix client instance
 * @param {string} userId - The user ID to find a DM with
 * @returns {Promise<string|null>} - The room ID if found, null otherwise
 */
const findExistingDMRoom = async (client, userId) => {
  try {
    // Check m.direct account data
    const directEvent = client.getAccountData('m.direct');
    const directContent = directEvent?.getContent() || {};

    const roomIds = directContent[userId];
    
    if (!roomIds || !Array.isArray(roomIds) || roomIds.length === 0) {
      return null;
    }

    // Return the first existing room (typically there's only one)
    // Filter to ensure the room actually exists and we're a member
    for (const roomId of roomIds) {
      const room = client.getRoom(roomId);
      if (room) {
        const membership = room.getMyMembership();
        // Only consider joined rooms as existing DMs
        if (membership === 'join') {
          return roomId;
        }
      }
    }

    return null;
  } catch (error) {
    logger.error('[findExistingDMRoom] Error finding existing DM:', error);
    return null;
  }
};

/**
 * Wait for a room to appear in the client's room list after creation.
 * This ensures the room is fully synced before we try to use it.
 * 
 * @param {MatrixClient} client - The Matrix client instance
 * @param {string} roomId - The room ID to wait for
 * @param {number} timeout - Maximum time to wait in milliseconds (default: 10000)
 * @returns {Promise<void>}
 */
const waitForRoomSync = (client, roomId, timeout = 10000) => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();

    // Check if room already exists
    if (client.getRoom(roomId)) {
      logger.debug('[waitForRoomSync] Room already exists in client');
      resolve();
      return;
    }

    // Set up timeout
    const timeoutId = setTimeout(() => {
      client.off('Room', onRoom);
      reject(new Error('Timeout waiting for room to sync'));
    }, timeout);

    // Listen for the room to be added
    const onRoom = (room) => {
      if (room.roomId === roomId) {
        logger.debug('[waitForRoomSync] Room synced successfully');
        clearTimeout(timeoutId);
        client.off('Room', onRoom);
        resolve();
      }

      // Also check if we've exceeded timeout
      if (Date.now() - startTime > timeout) {
        clearTimeout(timeoutId);
        client.off('Room', onRoom);
        reject(new Error('Timeout waiting for room to sync'));
      }
    };

    client.on('Room', onRoom);
  });
};

export default useAddContact;
